import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, HostBinding } from '@angular/core';
import { SignatureOptions } from '../../../../vendors/directus/interfaces/form.interface';

@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.scss'],
  styles: [
    `
      /* Import Dancing Script font directly at component level */
      @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
    `,
  ],
})
export class SignaturePadComponent implements OnInit, AfterViewInit {
  @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
  @Output() beginSign = new EventEmitter<void>();
  @Output() endSign = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();

  @Input() options: SignatureOptions = {
    width: 300,
    height: 200,
    backgroundColor: '#ffffff',
    penColor: '#000000',
    penWidth: 2,
  };

  @Input() fieldName: string = '';

  // For validation styling via ngClass
  @HostBinding('class.signature-container') containerClass = true;

  cursorPosition = { x: 0, y: 0 };
  showCursorIndicator = false;

  // For auto-generated signatures
  nameInput: string = '';
  showAutoComplete: boolean = true; // Always enabled

  // Hardcoded font properties
  private readonly fontFamily: string = '"Dancing Script"';
  private readonly defaultFontFamily: string =
    '"Segoe Script", "Snell Roundhand", "Brush Script MT", "Lucida Handwriting", "Comic Sans MS"';
  private readonly fontSize: number = 48;
  private readonly fontWeight: number = 700;
  private fontLoaded: boolean = false;

  private context: CanvasRenderingContext2D | null = null;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  isEmpty = true;

  constructor() {
    // We'll check if the font is loaded after initialization
  }

  ngOnInit(): void {
    // Ensure the canvas element has the data-field-name attribute
    setTimeout(() => {
      if (this.fieldName && this.signatureCanvas?.nativeElement) {
        this.signatureCanvas.nativeElement.setAttribute('data-field-name', this.fieldName);
      }
    });

    // Ensure the width never exceeds screen width
    this.adjustWidthToScreen();
  }

  ngAfterViewInit(): void {
    const canvas = this.signatureCanvas.nativeElement;
    this.context = canvas.getContext('2d');

    if (this.context) {
      // Configure the canvas
      this.context.fillStyle = this.options.backgroundColor || '#ffffff';
      this.context.fillRect(0, 0, canvas.width, canvas.height);

      this.context.strokeStyle = this.options.penColor || '#000000';
      this.context.lineWidth = this.options.penWidth || 2;
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';

      // Test render a single character to force font loading
      this.context.font = `${this.fontWeight} ${this.fontSize}px "${this.fontFamily}", cursive`;
      this.context.fillText('J', -100, -100); // Draw off-canvas to be invisible
    }

    // Add window resize listener
    window.addEventListener('resize', this.adjustWidthToScreen.bind(this));
  }

  // Ensure signature pad fits within screen
  private adjustWidthToScreen(): void {
    if (window.innerWidth < this.options.width) {
      this.options = {
        ...this.options,
        width: Math.min(window.innerWidth - 40, 300), // 40px padding/margin allowance
      };
    }
  }

  // Mouse event handlers
  onMouseDown(event: MouseEvent): void {
    this.isDrawing = true;
    const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
    const canvasScale = this.signatureCanvas.nativeElement.width / rect.width;

    // For pencil cursor, the tip is typically at the bottom left
    // Adjust offsets: move left by 6px, down by 1px
    this.lastX = (event.clientX - rect.left - 6) * canvasScale;
    this.lastY = (event.clientY - rect.top + 1) * canvasScale;

    this.beginSign.emit();
    this.isEmpty = false;
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDrawing || !this.context) return;

    this.context.beginPath();
    this.context.moveTo(this.lastX, this.lastY);

    const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();
    const canvasScale = this.signatureCanvas.nativeElement.width / rect.width;

    // Apply the same offset correction
    const currentX = (event.clientX - rect.left - 14) * canvasScale;
    const currentY = (event.clientY - rect.top + 2) * canvasScale;

    this.context.lineTo(currentX, currentY);
    this.context.stroke();

    this.lastX = currentX;
    this.lastY = currentY;
  }

  onMouseUp(): void {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.endSign.emit(this.toDataURL());
    }
  }

  onMouseEnter(): void {
    this.showCursorIndicator = true;
  }

  onMouseLeave(): void {
    this.showCursorIndicator = false;
    if (this.isDrawing) {
      this.isDrawing = false;
      this.endSign.emit(this.toDataURL());
    }
  }

  // Touch event handlers (for mobile devices)
  onTouchStart(event: TouchEvent): void {
    event.preventDefault();

    if (event.touches.length === 1) {
      const touch = event.touches[0];
      const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();

      // Calculate touch coordinates with better precision
      // Apply a slight offset to better align with the actual finger position
      const canvasScale = this.signatureCanvas.nativeElement.width / rect.width;
      this.lastX = (touch.clientX - rect.left) * canvasScale;
      this.lastY = (touch.clientY - rect.top - 5) * canvasScale; // -5 offset to position above the finger

      this.isDrawing = true;
      this.beginSign.emit();
      this.isEmpty = false;
    }
  }

  onTouchMove(event: TouchEvent): void {
    event.preventDefault();

    if (this.isDrawing && event.touches.length === 1 && this.context) {
      const touch = event.touches[0];
      const rect = this.signatureCanvas.nativeElement.getBoundingClientRect();

      // Calculate touch coordinates with better precision
      // Apply a slight offset to better align with the actual finger position
      const canvasScale = this.signatureCanvas.nativeElement.width / rect.width;
      const currentX = (touch.clientX - rect.left) * canvasScale;
      const currentY = (touch.clientY - rect.top - 5) * canvasScale; // -5 offset to position above the finger

      this.context.beginPath();
      this.context.moveTo(this.lastX, this.lastY);
      this.context.lineTo(currentX, currentY);
      this.context.stroke();

      this.lastX = currentX;
      this.lastY = currentY;
    }
  }

  onTouchEnd(): void {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.endSign.emit(this.toDataURL());
    }
  }

  // Generate signature based on name
  generateSignature(): void {
    if (!this.nameInput || !this.context) return;

    this.clear();

    const canvas = this.signatureCanvas.nativeElement;

    // Try different font declarations to avoid system font fallback
    try {
      // Set font properties with several fallbacks and browser-specific syntax
      const fontDeclaration = `${this.fontWeight} ${this.fontSize}px ${this.fontFamily}, cursive`;
      this.context.font = fontDeclaration;
      console.log('Using font:', fontDeclaration);
    } catch (e) {
      // If there's an error, try a simpler declaration
      this.context.font = `${this.fontWeight} ${this.fontSize}px ${this.defaultFontFamily}, cursive`;
      console.log('Using fallback font due to error:', e);
    }

    this.context.fillStyle = this.options.penColor || '#000000';
    this.context.textBaseline = 'middle';

    // Calculate position to center the signature
    const textMetrics = this.context.measureText(this.nameInput);
    const textWidth = textMetrics.width;
    const x = (canvas.width - textWidth) / 2;
    const y = canvas.height / 2;

    // Draw the signature
    this.context.fillText(this.nameInput, x, y);

    // Emit signature data
    this.isEmpty = false;
    this.endSign.emit(this.toDataURL());
  }

  // Handle input changes manually instead of using ngModel
  onNameInputChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.nameInput = inputElement.value;
    this.generateSignature();
  }

  // Method to toggle between manual drawing and auto-complete
  toggleAutoComplete(show: boolean): void {
    this.showAutoComplete = show;
    this.clear();
  }

  // Public methods
  clear(): void {
    if (!this.context) {
      console.error('Cannot clear signature pad: canvas context is null');
      return;
    }

    // Get canvas dimensions
    const canvas = this.signatureCanvas.nativeElement;
    const width = canvas.width;
    const height = canvas.height;

    // Clear the entire canvas by filling with the background color
    this.context.fillStyle = this.options.backgroundColor || '#ffffff';
    this.context.clearRect(0, 0, width, height);
    this.context.fillRect(0, 0, width, height);

    // Reset drawing settings
    this.context.strokeStyle = this.options.penColor || '#000000';
    this.context.lineWidth = this.options.penWidth || 2;
    this.context.lineCap = 'round';
    this.context.lineJoin = 'round';

    // Reset state
    this.isEmpty = true;
    this.cleared.emit();

    console.log('Signature pad cleared successfully');
  }

  toDataURL(type: string = 'image/png', quality: number = 1): string {
    return this.signatureCanvas.nativeElement.toDataURL(type, quality);
  }

  fromDataURL(dataUrl: string): void {
    if (!this.context) return;

    const image = new Image();
    image.onload = () => {
      this.context?.drawImage(image, 0, 0);
      this.isEmpty = false;
    };
    image.src = dataUrl;
  }

  isCanvasEmpty(): boolean {
    return this.isEmpty;
  }

  clearSignature(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isEmpty) return; // Do nothing if already empty

    // Reset the name input for auto-generated signatures
    this.nameInput = '';

    this.clear();
  }
}
